import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Eye, MapPin, DollarSign, TrendingUp, Star, FileText,
  Shield, Filter, ArrowUpDown, Lock, Award, CheckCircle, Users,
  AlertCircle, Building2, ExternalLink
} from "lucide-react";
import { Card } from "../shared/UIComponents";
import { fmtUSD } from "../../utils/currency";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LOAN_TYPE_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR",
  cash_out: "Cash-Out Refi", construction: "Construction",
  ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

const FALLBACK_PHOTOS = {
  fix_flip: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=600&q=80",
  bridge: "https://images.unsplash.com/photo-1582407947092-a75e4d4aee11?auto=format&fit=crop&w=600&q=80",
  dscr: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80",
  construction: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80",
  multifamily: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=600&q=80",
  commercial: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80",
  default: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80",
};

export default function MarketplacePreview() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedDeal, setSelectedDeal] = useState(null);
  const navigate = useNavigate();

  const headers = { Authorization: `Bearer ${localStorage.getItem("ic_token")}` };

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketplace-preview`, { headers });
      setDeals(res.data.deals || []);
    } catch (err) {
      toast.error("Failed to load marketplace preview");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const filtered = deals.filter(d => {
    if (filter === "with_brief") return !!d.lender_brief;
    if (filter === "no_brief") return !d.lender_brief;
    if (filter === "active") return d.stage !== "funded" && d.stage !== "active";
    if (filter === "funded") return d.stage === "funded" || d.stage === "active";
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "amount") return (b.requested_loan_amount || 0) - (a.requested_loan_amount || 0);
    if (sortBy === "interest") return (b.total_interest || 0) - (a.total_interest || 0);
    if (sortBy === "newest") return new Date(b.created_at) - new Date(a.created_at);
    return 0;
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-[#888]">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading marketplace preview...
    </div>
  );

  return (
    <div data-testid="marketplace-preview">
      {/* Admin Preview Banner */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[#222] flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#D4AF37]" /> Lender Marketplace Preview
          </h2>
          <p className="text-xs text-[#888] mt-0.5">
            This is exactly what lenders see when browsing the deal marketplace.
          </p>
        </div>
        <div className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-[10px] font-semibold text-amber-700" data-testid="admin-preview-badge">
          Admin Preview Mode
        </div>
      </div>

      {/* Stats Row — mirrors lender marketplace stats */}
      <div className="grid gap-4 sm:grid-cols-4 mb-6">
        {[
          { label: "Visible Deals", value: deals.length, icon: TrendingUp, color: "text-emerald-600" },
          { label: "With AI Brief", value: deals.filter(d => d.lender_brief).length, icon: Star, color: "text-amber-600" },
          { label: "Lender Interest", value: deals.reduce((s, d) => s + (d.total_interest || 0), 0), icon: Users, color: "text-purple-600" },
          { label: "Total Volume", value: `$${(deals.reduce((s, d) => s + (d.requested_loan_amount || 0), 0) / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card hover={false}>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-cream p-2.5 border border-cream-warm"><s.icon className="h-5 w-5 text-emerald-700" /></div>
                <div>
                  <div className="text-2xl font-bold text-[#0A0A0A]">{s.value}</div>
                  <div className="text-xs text-[#888] uppercase tracking-wider">{s.label}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Filter & Sort — matching lender marketplace style */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex gap-2">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active Deals" },
            { key: "funded", label: "Funded / Closed" },
            { key: "with_brief", label: "AI Brief Ready" },
            { key: "no_brief", label: "Missing Brief" },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filter === f.key ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-[#EDE8DC] text-[#666] hover:border-emerald-200"}`}
              data-testid={`mp-filter-${f.key}`}>
              <Filter className="h-3 w-3 inline mr-1" />{f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {[
            { key: "newest", label: "Newest" },
            { key: "amount", label: "Highest Amount" },
            { key: "interest", label: "Most Interest" },
          ].map(s => (
            <button key={s.key} onClick={() => setSortBy(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${sortBy === s.key ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-white border-[#EDE8DC] text-[#666] hover:border-blue-200"}`}
              data-testid={`mp-sort-${s.key}`}>
              <ArrowUpDown className="h-3 w-3 inline mr-1" />{s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Deal Cards Grid — mirrors lender marketplace card design */}
      {sorted.length === 0 ? (
        <Card hover={false}>
          <div className="text-center py-12" data-testid="marketplace-empty">
            <Shield className="h-12 w-12 text-emerald-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">No Deals in Marketplace</h3>
            <p className="text-sm text-[#666]">
              Deals appear here when they reach lender-eligible stages and have been approved for marketplace visibility.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {sorted.map((deal, i) => {
            const photo = deal.photo_url || FALLBACK_PHOTOS[deal.loan_type] || FALLBACK_PHOTOS.default;
            const loanAmt = deal.requested_loan_amount || deal.purchase_price || 0;
            const ltvPct = deal.ltv ? `${(deal.ltv * 100).toFixed(0)}%` : "N/A";
            const arvDisplay = deal.arv ? `$${(deal.arv / 1e3).toFixed(0)}K` : "N/A";
            const isExpanded = selectedDeal === deal.lead_id;

            return (
              <motion.div key={deal.lead_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <div
                  className={`rounded-2xl border overflow-hidden transition-all group cursor-pointer ${isExpanded ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/30 shadow-lg" : "border-[#EDE8DC] bg-white hover:shadow-[0_8px_30px_rgba(6,95,70,0.1)] hover:border-emerald-200"}`}
                  onClick={() => setSelectedDeal(isExpanded ? null : deal.lead_id)}
                  data-testid={`mp-deal-card-${deal.lead_id}`}
                >
                  {/* Hero Image — same as lender marketplace */}
                  <div className="relative h-36 overflow-hidden">
                    <img src={photo} alt={deal.property_address || "Property"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.src = FALLBACK_PHOTOS.default; }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    {/* Top-left: Loan Type + Status */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5">
                      <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-white/90 text-[#0A0A0A] backdrop-blur-sm shadow-sm">
                        {LOAN_TYPE_LABELS[deal.loan_type] || deal.loan_type}
                      </span>
                      {(deal.stage === "funded" || deal.stage === "active") && (
                        <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/90 text-white backdrop-blur-sm">
                          {deal.stage === "funded" ? "Funded" : "Active"}
                        </span>
                      )}
                    </div>
                    {/* Top-right: Interest count */}
                    {deal.total_interest > 0 && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-blue-500/20 text-blue-200 backdrop-blur-sm border border-blue-400/30">
                          <Users className="h-2.5 w-2.5 inline mr-0.5" />{deal.total_interest} interested
                        </span>
                      </div>
                    )}
                    {/* Bottom overlay: Address */}
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="text-white text-sm font-semibold truncate">{deal.property_address || "Address Pending"}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {deal.state && <span className="text-[10px] text-white/70 flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{deal.state}</span>}
                        {deal.deal_number && <span className="text-[10px] text-white/50 font-mono">#{deal.deal_number}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    {/* Key Metrics — 4 columns, same as lender marketplace */}
                    <div className="grid grid-cols-4 gap-1.5 mb-3">
                      <div className="rounded-lg bg-[#faf8f3] p-2 border border-[#EDE8DC] text-center">
                        <div className="text-[8px] text-[#999] uppercase">Loan Ask</div>
                        <div className="text-xs font-bold text-[#0A0A0A]">${(loanAmt / 1e3).toFixed(0)}K</div>
                      </div>
                      <div className="rounded-lg bg-[#faf8f3] p-2 border border-[#EDE8DC] text-center">
                        <div className="text-[8px] text-[#999] uppercase">LTV</div>
                        <div className={`text-xs font-bold ${deal.ltv && deal.ltv <= 0.65 ? "text-emerald-600" : deal.ltv && deal.ltv <= 0.75 ? "text-amber-600" : deal.ltv ? "text-red-500" : "text-[#999]"}`}>{ltvPct}</div>
                      </div>
                      <div className="rounded-lg bg-[#faf8f3] p-2 border border-[#EDE8DC] text-center">
                        <div className="text-[8px] text-[#999] uppercase">ARV</div>
                        <div className="text-xs font-bold text-[#0A0A0A]">{arvDisplay}</div>
                      </div>
                      <div className="rounded-lg bg-[#faf8f3] p-2 border border-[#EDE8DC] text-center">
                        <div className="text-[8px] text-[#999] uppercase">FICO</div>
                        <div className="text-xs font-bold text-[#0A0A0A]">{deal.fico_estimate || "N/A"}</div>
                      </div>
                    </div>

                    {/* Details Row */}
                    <div className="flex items-center gap-2 text-[10px] text-[#888] mb-3 flex-wrap">
                      {deal.experience > 0 && <span>Exp: {deal.experience} projects</span>}
                      {deal.exit_strategy && <><span className="w-px h-2.5 bg-[#ddd]" /><span>{deal.exit_strategy}</span></>}
                      {deal.lender_views > 0 && <><span className="w-px h-2.5 bg-[#ddd]" /><span><Eye className="h-2.5 w-2.5 inline mr-0.5" />{deal.lender_views} views</span></>}
                    </div>

                    {/* Lender Brief (AI-generated) — same as lender sees */}
                    {deal.lender_brief && (
                      <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-3 mb-3">
                        {deal.lender_brief.headline && (
                          <div className="text-[10px] font-bold text-emerald-800 mb-1">{deal.lender_brief.headline}</div>
                        )}
                        {deal.lender_brief.opportunity_summary && (
                          <p className="text-[9px] text-[#666] leading-relaxed">{deal.lender_brief.opportunity_summary}</p>
                        )}
                        {deal.lender_brief.key_highlights?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {deal.lender_brief.key_highlights.slice(0, 3).map((h, idx) => (
                              <span key={idx} className="px-1.5 py-0.5 rounded text-[7px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                                <CheckCircle className="h-2 w-2 inline mr-0.5" />{typeof h === "string" ? h : h.label ? `${h.label}: ${h.value}` : JSON.stringify(h)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Bar — admin view replaces lender actions with status */}
                    <div className="pt-3 border-t border-[#EDE8DC] flex items-center justify-between">
                      <span className="text-[10px] text-[#aaa] font-mono">{deal.deal_number}</span>
                      <span className={`px-2.5 py-1 rounded-lg text-[9px] font-bold border ${
                        deal.lender_brief
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-gray-50 text-gray-500 border-gray-200"
                      }`}>
                        {deal.lender_brief ? "AI Brief Ready" : "No Brief"}
                      </span>
                    </div>
                  </div>

                  {/* Admin Intel (expanded on click) — admin-only section */}
                  {isExpanded && (
                    <div className="px-4 py-3 bg-amber-50/40 border-t border-amber-100 space-y-2" data-testid={`mp-admin-detail-${deal.lead_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-[9px] font-bold text-amber-700 uppercase tracking-wider">Admin Intel</div>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/entity/deal/${deal.lead_id}`); }}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                          data-testid={`mp-view-deal-${deal.lead_id}`}
                        >
                          <ExternalLink className="h-3 w-3" /> Open Deal 360
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px]">
                        <div><span className="text-[#888]">Stage:</span> <span className="font-medium text-[#222]">{deal.stage}</span></div>
                        <div><span className="text-[#888]">Lender Views:</span> <span className="font-medium text-[#222]">{deal.lender_views || 0}</span></div>
                        <div><span className="text-[#888]">Interested:</span> <span className="font-medium text-[#222]">{deal.total_interest || 0}</span></div>
                        <div><span className="text-[#888]">Purchase:</span> <span className="font-medium text-[#222]">{deal.purchase_price ? fmtUSD(deal.purchase_price) : "N/A"}</span></div>
                      </div>
                      {!deal.lender_brief && (
                        <div className="flex items-center gap-1.5 text-[9px] text-amber-600 mt-1">
                          <AlertCircle className="h-3 w-3" /> No AI lender brief — generate from Underwriting tab
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
